import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { paths } from '@/routes/paths';
import { useCurrentAdmin } from '@/hooks/useAuth';
import styles from './SidebarNav.module.css';

interface NavItem {
  to: string;
  label: string;
  group: 'People' | 'Risk' | 'Money' | 'Content' | 'System';
  supportAllowed?: boolean;
}

// Layout grouped as required by the spec.
const NAV: NavItem[] = [
  { to: paths.dashboard, label: 'Dashboard', group: 'People', supportAllowed: true },

  { to: paths.users, label: 'Users', group: 'People', supportAllowed: true },
  { to: paths.companies, label: 'Companies', group: 'People' },
  { to: paths.bookingsAudit, label: 'Bookings', group: 'People', supportAllowed: true },

  { to: paths.safetyScores, label: 'Safety scores', group: 'Risk', supportAllowed: true },
  { to: paths.countries, label: 'Countries & lists', group: 'Risk' },
  { to: paths.weights, label: 'Severity weights', group: 'Risk' },
  { to: paths.newsSources, label: 'News sources', group: 'Risk' },
  { to: paths.kimi, label: 'Kimi AI', group: 'Risk' },
  { to: paths.simulation, label: 'Simulation', group: 'Risk' },

  { to: paths.plans, label: 'Subscription plans', group: 'Money' },
  { to: paths.paymentsAudit, label: 'Payments audit', group: 'Money', supportAllowed: true },

  { to: paths.support, label: 'Support tickets', group: 'Content', supportAllowed: true },
  { to: paths.pushBroadcasts, label: 'Push broadcasts', group: 'Content' },

  { to: paths.featureToggles, label: 'Feature toggles', group: 'System' },
  { to: paths.config, label: 'System config', group: 'System' },
  { to: paths.auditLog, label: 'Audit log', group: 'System', supportAllowed: true },
  { to: paths.system, label: 'System actions', group: 'System' },
];

interface Props {
  collapsed?: boolean;
}

export function SidebarNav({ collapsed }: Props) {
  const admin = useCurrentAdmin();
  const isSupport = admin?.role === 'support';

  const groups: Record<string, NavItem[]> = {};
  for (const item of NAV) {
    if (isSupport && !item.supportAllowed) continue;
    (groups[item.group] ??= []).push(item);
  }

  return (
    <aside className={clsx(styles.sidebar, collapsed && styles.collapsed)}>
      <div className={styles.brand}>
        <span className={styles.logo}>S</span>
        {!collapsed && (
          <div className={styles.brandText}>
            <strong>SYRON</strong>
            <small>Admin</small>
          </div>
        )}
      </div>

      <nav className={styles.nav}>
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className={styles.group}>
            {!collapsed && <div className={styles.groupLabel}>{group}</div>}
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === paths.dashboard}
                className={({ isActive }) =>
                  clsx(styles.link, isActive && styles.linkActive)
                }
                title={item.label}
              >
                <span className={styles.dot} aria-hidden="true" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
